import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../common/Sidebar';
import Pagination from '../common/Pagination';
import '../../styles/ProfessorDashboard.css';
import api from '../../utils/api';

interface Course {
    id: string;
    isActive: boolean;
    professorId: string;
    school: string;
    title: string;
}

interface Class {
    id: string;
    createdAt: string;
    updatedAt: string;
    course: {
        id: string;
        title: string;
        isActive: boolean;
        professorId: string;
        school: string;
    };
    courseId: string;
    endTime?: string;
    layout: string;
    password: string;
    professor: {
        id: string;
        school: string;
    };
    status?: string;
    targetLanguages: string[];
    title?: string;
    limit: string;
    page: string;
    total: string;
    totalPages: string;
}

interface User {
    id: string;
    username: string;
    role: string;
    school?: string;
}

interface ProfessorDashboardProps {
    user: User | null;
}

// ClassLayout 타입 정의 (기존 인터페이스 아래에 추가)
enum ClassLayout {
    LECTURE = 'LECTURE', // 강의 레이아웃
    CONSULTATION = 'CONSULTATION', // 1대1 상담 레이아웃
    PRESENTATION = 'PRESENTATION', // 발표 레이아웃
}

const ProfessorDashboard: React.FC<ProfessorDashboardProps> = ({ user }) => {
    const [activeSection, setActiveSection] = useState<'dashboard' | 'courses' | 'classes'>(
        'dashboard',
    );
    const [courses, setCourses] = useState<Course[]>([]);
    const [recentClasses, setRecentClasses] = useState<Class[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const [showAddCourseModal, setShowAddCourseModal] = useState(false);
    const [newCourseName, setNewCourseName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [showStartClassModal, setShowStartClassModal] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
    const [classPassword, setClassPassword] = useState('');
    const [classPasswordError, setClassPasswordError] = useState<string | null>(null);
    const [classLayout, setClassLayout] = useState<ClassLayout>(ClassLayout.LECTURE);
    const [showErrorReportModal, setShowErrorReportModal] = useState(false);
    const [errorReportForm, setErrorReportForm] = useState({
        content: '',
    });
    const [errorReportSuccess, setErrorReportSuccess] = useState<string | null>(null);
    const [errorReportError, setErrorReportError] = useState<string | null>(null);
    // 페이지네이션 상태 추가
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalClasses, setTotalClasses] = useState(0);
    const [classesPerPage, setClassesPerPage] = useState(10);
    const [isLoadingClasses, setIsLoadingClasses] = useState(false);

    // fetchCourses 함수를 컴포넌트 레벨로 이동
    const fetchCourses = async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/courses/my');

            // 응답 구조에 따라 데이터 추출
            let coursesData;
            if (response.data.result) {
                coursesData = response.data.result;
            } else {
                coursesData = response.data;
            }

            // 배열 확인
            if (!Array.isArray(coursesData)) {
                coursesData = [];
            }

            // 각 과목 객체에 필수 필드가 있는지 확인
            const validCoursesData = coursesData.map(item => {
                // 필수 필드 보장
                return {
                    id: item.id || `unknown-${Math.random().toString(36).substr(2, 9)}`,
                    title: item.title || '(과목명 없음)',
                    isActive: item.isActive !== undefined ? item.isActive : true,
                    professorId: item.professorId || '',
                    school: item.school || '',
                };
            });

            setCourses(validCoursesData);
        } catch (err: any) {
            setError('과목 목록을 불러오는데 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // useEffect에서는 이 함수를 호출
    useEffect(() => {
        fetchCourses();
    }, []);

    // 최근 강의 목록 가져오기 - 페이지네이션 적용
    useEffect(() => {
        const fetchRecentClasses = async () => {
            try {
                setIsLoadingClasses(true);
                const response = await api.get('/classes/recent', {
                    params: {
                        page: currentPage,
                        limit: classesPerPage,
                    },
                });

                if (response.data.result) {
                    setRecentClasses(response.data.result.data || []);
                    setTotalPages(Number(response.data.result.totalPages) || 1);
                    setTotalClasses(Number(response.data.result.total) || 0);
                } else {
                    setRecentClasses([]);
                }
            } catch (err: any) {
                console.error('Error fetching recent classes:', err);
            } finally {
                setIsLoadingClasses(false);
            }
        };

        fetchRecentClasses();
    }, [currentPage, classesPerPage]);

    // 새 강의 시작하기
    const handleStartNewClass = (courseId: string) => {
        // 선택된 과목 찾기
        const course = courses.find(c => c.id === courseId);
        if (course) {
            setSelectedCourse(course);
            setShowStartClassModal(true);
            setClassPassword('');
            setClassPasswordError(null);
            setClassLayout(ClassLayout.LECTURE); // 기본값으로 강의 레이아웃 설정
        }
    };

    // 강의 기록 보기
    const handleViewClassRecord = (classId: string) => {
        navigate(`/class/record/${classId}`);
    };

    // 강의 재개설
    const handleReopenClass = (classId: string) => {
        navigate(`/class/reopen/${classId}`);
    };

    // 과목 삭제 처리
    const handleDeleteCourse = (courseId: string) => {
        // 여기에 과목 삭제 API 호출 로직 추가
        if (window.confirm('정말로 이 과목을 삭제하시겠습니까?')) {
            setIsLoading(true);
            // 예시: API 호출
            api.delete(`/courses/${courseId}`)
                .then(() => {
                    // 성공 시 과목 목록에서 제거
                    setCourses(prevCourses => prevCourses.filter(course => course.id !== courseId));
                })
                .catch(err => {
                    setError('과목 삭제에 실패했습니다.');
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    // 모달 열기 함수
    const handleAddNewCourse = () => {
        setShowAddCourseModal(true);
        setNewCourseName('');
        setModalError(null);
    };

    // 모달 닫기 함수
    const handleCloseModal = () => {
        setShowAddCourseModal(false);
    };

    // 과목 추가 함수
    const handleSubmitNewCourse = async () => {
        // 입력 검증
        if (!newCourseName.trim()) {
            setModalError('과목 이름을 입력해주세요.');
            return;
        }

        try {
            setIsSubmitting(true);
            setModalError(null);

            // 백엔드 API 호출
            const response = await api.post('/courses', {
                title: newCourseName.trim(),
            });

            // 새로운 과목 객체 생성
            const newCourse = {
                id: response.data.id || response.data.result?.id || `temp-${Date.now()}`,
                title: newCourseName.trim(), // 입력한 이름을 직접 사용
                isActive: true,
                professorId: user?.id || '',
                school: user?.school || '',
            };

            // 과목 목록 업데이트 - 함수형 업데이트 사용
            setCourses(prevCourses => [...prevCourses, newCourse]);

            // 모달 닫기
            setShowAddCourseModal(false);
            setNewCourseName('');

            // 과목 목록 다시 불러오기
            fetchCourses();

            // 선택적: 성공 메시지 표시
            // toast 또는 alert 등으로 성공 메시지 표시
        } catch (err: any) {
            setModalError(err.response?.data?.message || '과목 추가에 실패했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 강의 시작 모달 닫기 함수
    const handleCloseClassModal = () => {
        setShowStartClassModal(false);
        setSelectedCourse(null);
        setClassPassword('');
        setClassPasswordError(null);
        setClassLayout(ClassLayout.LECTURE); // 초기화
    };

    // 강의 시작 제출 함수
    const handleSubmitNewClass = async () => {
        // 비밀번호 검증 (4자리 숫자)
        if (!classPassword.trim()) {
            setClassPasswordError('비밀번호를 입력해주세요.');
            return;
        }

        if (!/^\d{4}$/.test(classPassword)) {
            setClassPasswordError('비밀번호는 4자리 숫자여야 합니다.');
            return;
        }

        try {
            setIsSubmitting(true);
            setClassPasswordError(null);

            // 백엔드 API 호출 (강의 생성)
            const response = await api.post('/classes', {
                title: selectedCourse?.title,
                courseId: selectedCourse?.id,
                password: classPassword,
                layout: classLayout,
            });

            // 모달 닫기
            setShowStartClassModal(false);

            // 클래스 ID 가져오기
            const classId = response.data.result?.id || response.data.id;

            // 레이아웃에 따라 다른 페이지로 이동
            if (classLayout === ClassLayout.LECTURE) {
                // 강의 레이아웃인 경우 기존 강의 페이지로 이동
                navigate(`/class/${classId}`);
            } else if (classLayout === ClassLayout.CONSULTATION) {
                // 상담 레이아웃인 경우 상담 페이지로 이동
                await api.post(`/classes/${classId}/start`);
                navigate(`/consultation/${classId}`);
            } else if (classLayout === ClassLayout.PRESENTATION) {
                // 발표 레이아웃인 경우도 강의 페이지로 이동 (레이아웃은 서버에서 구분)
                navigate(`/class/${classId}`);
            }
        } catch (err: any) {
            console.error('Error creating class:', err);
            setClassPasswordError(err.response?.data?.message || '강의 생성에 실패했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 비밀번호 입력 핸들러 (숫자만 입력 가능하도록)
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // 숫자만 입력 가능하도록 필터링
        if (value === '' || /^\d+$/.test(value)) {
            // 최대 4자리까지만 입력 가능
            setClassPassword(value.slice(0, 4));
        }
    };

    const handleErrorReportChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setErrorReportForm({
            content: e.target.value,
        });
    };

    const handleSubmitErrorReport = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorReportError(null);
        setErrorReportSuccess(null);

        // 내용이 비어있는지 검사
        if (!errorReportForm.content.trim()) {
            setErrorReportError('오류 내용을 입력해주세요.');
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await api.post('/reports', {
                content: errorReportForm.content,
            });

            if (response.status === 200 || response.status === 201) {
                setErrorReportSuccess('오류가 성공적으로 신고되었습니다.');
                // 모달 폼 초기화
                setErrorReportForm({
                    content: '',
                });

                // 3초 후 모달 닫기
                setTimeout(() => {
                    setShowErrorReportModal(false);
                    setErrorReportSuccess(null);
                }, 3000);
            }
        } catch (error) {
            setErrorReportError('오류 신고에 실패했습니다. 다시 시도해주세요.');
            console.error('오류 신고 오류:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 페이지 변경 핸들러
    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // 사이드바 메뉴 아이템
    const sidebarItems = [
        {
            id: 'dashboard',
            label: '메인화면',
            icon: 'home',
            onClick: () => setActiveSection('dashboard'),
        },
        {
            id: 'profile',
            label: '프로필',
            icon: 'user',
            onClick: () => navigate('/profile'),
        },
        {
            id: 'error-report',
            label: '오류신고',
            icon: 'alert',
            onClick: () => setShowErrorReportModal(true),
        },
    ];

    // 모달 열림/닫힘 시 body 스크롤 제어
    useEffect(() => {
        const hasOpenModal = showAddCourseModal || showStartClassModal || showErrorReportModal;

        if (hasOpenModal) {
            // 모달이 열릴 때 현재 스크롤 위치 저장
            const scrollY = window.scrollY;
            document.body.style.top = `-${scrollY}px`;
            document.body.classList.add('modal-open');
        } else {
            // 모달이 닫힐 때 스크롤 위치 복원
            const scrollY = parseInt(document.body.style.top || '0', 10);
            document.body.classList.remove('modal-open');
            document.body.style.top = '';
            window.scrollTo(0, -scrollY);
        }

        // 컴포넌트 언마운트 시 정리
        return () => {
            document.body.classList.remove('modal-open');
            document.body.style.top = '';
        };
    }, [showAddCourseModal, showStartClassModal, showErrorReportModal]);

    return (
        <>
            <Sidebar items={sidebarItems} activeItem={activeSection} user={user} />

            {/* 오류 신고 모달 */}
            {showErrorReportModal && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h2>오류 신고</h2>
                        {errorReportError && (
                            <div className="error-message">{errorReportError}</div>
                        )}
                        {errorReportSuccess && (
                            <div className="success-message">{errorReportSuccess}</div>
                        )}
                        <form onSubmit={handleSubmitErrorReport}>
                            <div className="form-group">
                                <label htmlFor="errorContent">오류 내용</label>
                                <textarea
                                    id="errorContent"
                                    name="content"
                                    value={errorReportForm.content}
                                    onChange={handleErrorReportChange}
                                    required
                                    rows={5}
                                    placeholder="발생한 오류에 대해 자세히 설명해주세요."
                                />
                            </div>
                            <div className="button-group">
                                <button
                                    type="button"
                                    className="cancel-button"
                                    onClick={() => setShowErrorReportModal(false)}
                                    disabled={isSubmitting}
                                >
                                    취소
                                </button>
                                <button
                                    type="submit"
                                    className="save-button"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? '처리 중...' : '신고하기'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <main className="dashboard-main">
                {activeSection === 'dashboard' && (
                    <div className="dashboard-overview">
                        <h2>교수 대시보드</h2>

                        {/* 과목 관리 섹션 */}
                        <section className="course-management-section">
                            <div className="section-header">
                                <h3>과목 관리</h3>
                            </div>

                            {isLoading ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>과목 정보를 불러오는 중...</p>
                                </div>
                            ) : error ? (
                                <div className="error-container">
                                    <div className="error-icon">!</div>
                                    <p className="error-message">{error}</p>
                                </div>
                            ) : (
                                <div className="courses-container">
                                    {courses.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-icon">📚</div>
                                            <h3>등록된 과목이 없습니다</h3>
                                            <p>새 과목을 추가하여 강의를 시작해보세요!</p>
                                            <button
                                                className="btn-primary"
                                                onClick={handleAddNewCourse}
                                            >
                                                첫 과목 추가하기
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="course-grid">
                                            {courses.map(course => (
                                                <div
                                                    key={course.id}
                                                    className="course-card"
                                                    onClick={() => handleStartNewClass(course.id)}
                                                >
                                                    <button
                                                        className="delete-icon-minimal"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleDeleteCourse(course.id);
                                                        }}
                                                        aria-label="과목 삭제"
                                                    >
                                                        ×
                                                    </button>
                                                    <h4 className="course-title">{course.title}</h4>
                                                    <button className="start-class-btn">
                                                        강의 시작
                                                    </button>
                                                </div>
                                            ))}
                                            {/* 새 과목 추가 카드 */}
                                            <div
                                                className="course-card add-card"
                                                onClick={handleAddNewCourse}
                                                role="button"
                                                tabIndex={0}
                                                aria-label="새 과목 추가"
                                            >
                                                <div className="add-card-content">
                                                    <div className="add-icon">+</div>
                                                    <p>새 과목 추가</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* 최근 강의 목록 섹션 */}
                        <section className="recent-classes-section">
                            <h3>최근 강의 목록</h3>
                            {isLoadingClasses ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>강의 정보를 불러오는 중...</p>
                                </div>
                            ) : recentClasses.length === 0 ? (
                                <p>최근 진행한 강의가 없습니다.</p>
                            ) : (
                                <>
                                    <div className="class-list">
                                        {recentClasses.map(classItem => (
                                            <div
                                                key={classItem.id}
                                                className="class-item"
                                                onClick={() => handleViewClassRecord(classItem.id)}
                                            >
                                                <div className="class-info">
                                                    <div className="class-header">
                                                        <span className="class-code">
                                                            #{classItem.id}
                                                        </span>
                                                        <h4>
                                                            {classItem.course?.title ||
                                                                classItem.title ||
                                                                '(제목 없음)'}
                                                        </h4>
                                                    </div>
                                                    <p className="class-date">
                                                        {new Intl.DateTimeFormat('ko-KR', {
                                                            timeZone: 'Asia/Seoul',
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true,
                                                        }).format(new Date(classItem.createdAt))}
                                                    </p>
                                                    <p className="class-students">
                                                        레이아웃:{' '}
                                                        {classItem.layout === 'LECTURE'
                                                            ? '강의'
                                                            : classItem.layout === 'CONSULTATION'
                                                            ? '1대1 상담'
                                                            : classItem.layout === 'PRESENTATION'
                                                            ? '발표'
                                                            : classItem.layout}
                                                    </p>
                                                    {classItem.status && (
                                                        <p className="class-status">
                                                            상태:{' '}
                                                            <span
                                                                className={`status-${classItem.status.toLowerCase()}`}
                                                            >
                                                                {classItem.status === 'ENDED'
                                                                    ? '종료됨'
                                                                    : classItem.status}
                                                            </span>
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="class-actions">
                                                    <button
                                                        className="btn-secondary view-record-btn"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleViewClassRecord(classItem.id);
                                                        }}
                                                    >
                                                        강의 기록 보기 →
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* 페이지네이션 컴포넌트 사용 */}
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        totalItems={totalClasses}
                                        onPageChange={handlePageChange}
                                        itemName="강의"
                                    />
                                </>
                            )}
                        </section>
                    </div>
                )}

                {activeSection === 'classes' && (
                    <div className="classes-management">
                        <h2>강의 관리</h2>
                        {/* 강의 관리 내용 */}
                    </div>
                )}
            </main>

            {/* 새 과목 추가 모달 */}
            {showAddCourseModal && (
                <div
                    className="modal-overlay"
                    onClick={e => {
                        // 모달 외부 클릭 시 닫기
                        if (e.target === e.currentTarget) handleCloseModal();
                    }}
                >
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>새 과목 추가</h3>
                            <button
                                className="modal-close-btn"
                                onClick={handleCloseModal}
                                aria-label="모달 닫기"
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="courseName">과목 이름</label>
                                <input
                                    type="text"
                                    id="courseName"
                                    value={newCourseName}
                                    onChange={e => setNewCourseName(e.target.value)}
                                    placeholder="과목 이름을 입력하세요"
                                    autoFocus
                                />
                            </div>
                            {modalError && <p className="modal-error">{modalError}</p>}
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-cancel"
                                onClick={handleCloseModal}
                                disabled={isSubmitting}
                            >
                                취소
                            </button>
                            <button
                                className="btn-submit"
                                onClick={handleSubmitNewCourse}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '추가 중...' : '추가'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showStartClassModal && selectedCourse && (
                <div
                    className="modal-overlay"
                    onClick={e => {
                        // 모달 외부 클릭 시 닫기
                        if (e.target === e.currentTarget) handleCloseClassModal();
                    }}
                >
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>강의 시작</h3>
                            <button
                                className="modal-close-btn"
                                onClick={handleCloseClassModal}
                                aria-label="모달 닫기"
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="courseName">과목 이름</label>
                                <input
                                    type="text"
                                    id="courseName"
                                    value={selectedCourse.title}
                                    disabled
                                    className="disabled-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="classPassword">강의 비밀번호 (4자리 숫자)</label>
                                <input
                                    type="text"
                                    id="classPassword"
                                    value={classPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="4자리 숫자 입력"
                                    autoFocus
                                    maxLength={4}
                                    pattern="\d{4}"
                                />
                                <small>학생들이 강의 참여 시 필요한 비밀번호입니다.</small>
                            </div>
                            <div className="form-group">
                                <label>강의 레이아웃</label>
                                <div className="layout-options">
                                    <div
                                        className={`layout-option ${
                                            classLayout === ClassLayout.LECTURE ? 'selected' : ''
                                        }`}
                                        onClick={() => setClassLayout(ClassLayout.LECTURE)}
                                    >
                                        <div className="layout-icon lecture-icon">🎓</div>
                                        <div className="layout-label">강의</div>
                                    </div>
                                    <div
                                        className={`layout-option ${
                                            classLayout === ClassLayout.CONSULTATION
                                                ? 'selected'
                                                : ''
                                        }`}
                                        onClick={() => setClassLayout(ClassLayout.CONSULTATION)}
                                    >
                                        <div className="layout-icon consultation-icon">👥</div>
                                        <div className="layout-label">1대1 상담</div>
                                    </div>
                                    <div
                                        className={`layout-option ${
                                            classLayout === ClassLayout.PRESENTATION
                                                ? 'selected'
                                                : ''
                                        }`}
                                        onClick={() => setClassLayout(ClassLayout.PRESENTATION)}
                                    >
                                        <div className="layout-icon presentation-icon">📊</div>
                                        <div className="layout-label">발표</div>
                                    </div>
                                </div>
                            </div>
                            {classPasswordError && (
                                <p className="modal-error">{classPasswordError}</p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-cancel"
                                onClick={handleCloseClassModal}
                                disabled={isSubmitting}
                            >
                                취소
                            </button>
                            <button
                                className="btn-submit"
                                onClick={handleSubmitNewClass}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '생성 중...' : '강의 시작'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProfessorDashboard;
